import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '../../lib/api.js';
import type { AssetDTOView, ImageRequest } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function ImageRequestDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const requestQuery = useQuery({
    queryKey: ['image-requests', id],
    queryFn: () => apiFetch<ImageRequest>(`/image-requests/${id}`),
  });

  const generate = useMutation({
    mutationFn: () =>
      apiFetch<AssetDTOView>(`/image-requests/${id}/generate`, {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['image-requests', id] }),
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Generation failed'),
  });

  if (requestQuery.isLoading) return <p>Loading…</p>;
  if (requestQuery.isError || !requestQuery.data) {
    return (
      <p role="alert" className="form-error">
        Image request not found.
      </p>
    );
  }
  const req = requestQuery.data.data;

  return (
    <section>
      <p>
        <Link to="/image-requests">← Back to image requests</Link>
      </p>
      <div className="page-head">
        <h1>Image request</h1>
        <StatusBadge status={req.status} />
      </div>

      <blockquote className="frozen-prompt">{req.promptSnapshot}</blockquote>
      <p className="muted">
        {req.aspectRatio} / {req.imageModelTier} · hash {req.promptHash.slice(0, 16)}…
      </p>

      {req.status === 'approved' && (
        <div className="review-panel">
          <p>This request is approved. Generating an image will call the paid provider.</p>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <button type="button" onClick={() => generate.mutate()} disabled={generate.isPending}>
            {generate.isPending ? 'Generating…' : 'Generate image'}
          </button>
        </div>
      )}

      {req.status === 'failed' && req.error && (
        <p role="alert" className="form-error">
          Generation failed: {req.error.code}
        </p>
      )}

      {req.status === 'completed' && req.assetId && (
        <div>
          <h2>Generated candidate</h2>
          <img
            className="asset-preview"
            src={`/api/v1/assets/${req.assetId}/content`}
            alt="Generated candidate"
          />
          <p>
            <Link to={`/assets/${req.assetId}`}>Review this asset →</Link>
          </p>
        </div>
      )}
    </section>
  );
}
